import React, {
  useState, useEffect, useRef, useMemo, useCallback,
} from 'react'
import { List, Map as ImmutableMap, fromJS } from 'immutable'
import { useSelector, useDispatch } from 'react-redux'
import { useFeatureFlagVariantKey } from '@posthog/react'
import { compose } from 'recompose'
import { POSTHOG_FEATURE_FLAGS, EventContextProvider, usePostHogEvent } from '../PostHog/index'
import { setAiGa4Event } from '../../services/event-tracking/actions'
import {
  AI_MODULE_VIEWED_GA4_EVENT,
  AI_MESSAGE_SENT_GA4_EVENT,
  AI_ERROR_RESPONSE_GA4_EVENT,
  AI_RECOMMENDATION_RECEIVED_GA4_EVENT,
} from '../../services/event-tracking/index'
import {
  connect as connectPage,
  PAGE_SEO_TYPE_LOCATION,
} from '../Page/connect'
import { connect as connectStaticText } from '../StaticText/connect'
import { selectAuth } from '../../services/auth/selectors'
import { selectPrimaryLanguage } from '../../services/user/selectors'
import TileV2Graphql from '../Tile.v2/Tile.v2Graphql/index'
import Row, { STYLES } from '../Row.v2/index'
import Button, { TYPES as BUTTON_TYPES } from '../Button/index'
import {
  createUpstreamContext,
  SCREEN_TYPE_SEARCH_PAGE,
} from '../../services/upstream-context/index'
import {
  buildTileData,
} from '../../services/community/identity/library/content-utils'
import type { OracleContentEntity } from '../../services/community/oracle/useOracleSearch'
import useAiSearchStream from '../../services/community/oracle/useAiSearchStream'
import type { SearchSort } from '../../services/community/oracle/useAiSearchStream'
import SearchForm from '../SearchForm/index'
import AiGuide from './AiGuide'
import SortBar from './SortBar'
import type { SortOption } from './SortBar'

const searchUpstreamContext = createUpstreamContext({
  screenType: SCREEN_TYPE_SEARCH_PAGE,
  screenParam: 'search-results',
  storeKey: 'search-results',
})

interface SearchPageProps {
  location: {
    query?: { q?: string; sort?: string }
    pathname?: string
    search?: string
  }
  history: Record<string, unknown>
  staticText: ImmutableMap<string, unknown>
}

function SearchPage ({
  location,
  history,
  staticText,
}: SearchPageProps): React.ReactElement {
  const [sortMode, setSortMode] = useState<SortOption>('relevance')

  const auth = useSelector(selectAuth)
  const jwt = auth.get('jwt') as string | null
  const language = (useSelector(selectPrimaryLanguage) || 'en') as string

  const q = location.query?.q || ''
  const dispatch = useDispatch()

  // PostHog flag gates AI overview visibility
  const aiSearchVariant = useFeatureFlagVariantKey(
    POSTHOG_FEATURE_FLAGS.AI_SEARCH_WEB,
  )
  const showOverview = aiSearchVariant === 'test'

  // ── PostHog + GA4 event tracking ────────────────────────────────
  const trackModuleViewed = usePostHogEvent('ai_module_viewed')
  const trackMessageSent = usePostHogEvent('ai_message_sent')
  const trackErrorResponse = usePostHogEvent('ai_error_response')

  useEffect(() => {
    trackModuleViewed('search_page')
    dispatch(setAiGa4Event(AI_MODULE_VIEWED_GA4_EVENT))
  }, [trackModuleViewed, dispatch])

  const {
    search: aiSearch,
    changeSort: aiChangeSort,
    refine: aiRefine,
    loadMore: aiLoadMore,
    results: aiResults,
    streamingText,
    state: streamState,
    statusKey,
    error: aiError,
    blacklistMessage,
    sendFeedback,
    turnId,
    contentTotalCount,
  } = useAiSearchStream(jwt, language, { enablePlaylistsAndProfiles: false })

  // Capture showOverview in a ref so the search effect doesn't re-fire
  // when PostHog resolves the flag (which would clear sessionIdRef and
  // break refinement continuity).
  const showOverviewRef = useRef(showOverview)
  showOverviewRef.current = showOverview

  // Trigger search when query changes
  useEffect(() => {
    if (q) {
      aiSearch(q, undefined, !showOverviewRef.current)
      setSortMode('relevance')
      trackMessageSent(q, 'Text', 'Text Field')
      dispatch(setAiGa4Event(AI_MESSAGE_SENT_GA4_EVENT))
    }
  }, [q, aiSearch, trackMessageSent, dispatch])

  // Track when content results arrive
  useEffect(() => {
    if (aiResults.content.length > 0) {
      dispatch(setAiGa4Event(AI_RECOMMENDATION_RECEIVED_GA4_EVENT))
    }
  }, [aiResults.content.length, dispatch])

  // Track search errors
  useEffect(() => {
    if (streamState === 'ERROR' && aiError) {
      trackErrorResponse(aiError, 'Text', 'Text Field')
      dispatch(setAiGa4Event(AI_ERROR_RESPONSE_GA4_EVENT))
    }
  }, [streamState, aiError, trackErrorResponse, dispatch])

  // ── Sort handler ───────────────────────────────────────────────
  const handleSortChange = useCallback((_option: SortOption, graphqlSort: SearchSort) => {
    setSortMode(_option)
    aiChangeSort(graphqlSort)
  }, [aiChangeSort])

  // Wrap refine to reset sort — refinements change what's relevant
  const handleRefine = useCallback((text: string) => {
    setSortMode('relevance')
    aiRefine(text)
  }, [aiRefine])

  // ── Tile data from search results ──────────────────────────────
  const tileData = useMemo(() => {
    const built = aiResults.content
      .filter((r) => r.path)
      .map((r) => {
        const heroUrl = r.heroImageUrl || r.thumbnailUrl || ''
        const thumbUrl = r.thumbnailUrl || r.heroImageUrl || ''
        const entity = {
          id: r.contentId,
          contentId: r.contentId,
          contentType: r.contentType || 'video',
          title: r.title || '',
          path: r.path || '',
          instructor: r.instructor || null,
          displayType: r.displayType || null,
          subType: r.displayType || null,
          studio: r.studio || null,
          featureMedia: {
            mediaId: r.mediaId || null,
            duration: r.durationSeconds || 0,
            contentPolicy: { accessCheck: { allowed: true } },
          },
          contentAssetReferences: thumbUrl
            ? [{ asset: { renderUrl: thumbUrl } }]
            : null,
          imageContentRelationships: [
            ...(heroUrl ? [{ type: 'd6-keyart-with-text', image: { url: heroUrl } }] : []),
            ...(thumbUrl ? [{ type: 'd6-keyart-no-text', image: { url: thumbUrl } }] : []),
          ],
          episodeNumber: r.episodeNumber || null,
          seasonNumber: r.seasonNumber || null,
          contentSeries: r.seriesTitle
            ? [{ title: r.seriesTitle, path: '', contentId: r.seriesContentId || 0 }]
            : null,
          references: (r.contentReferences || []).map((ref) => ({
            contentReferenceType: { type: ref.type },
            contentTo: [{
              title: ref.name, contentType: '', contentId: 0, path: '', language,
            }],
          })),
        }
        return buildTileData(entity as unknown as OracleContentEntity)
      })
      .filter((tile): tile is NonNullable<typeof tile> => !!tile)
    return List(
      built.map((tile) => fromJS(tile)),
    ) as List<ImmutableMap<string, unknown>>
  }, [aiResults.content, language])

  const hasMore = tileData.size < contentTotalCount

  // ── Static text ────────────────────────────────────────────────
  const loadMoreLabel = staticText.getIn(
    ['data', 'loadMore'],
    'Load More',
  ) as string
  const noVideoResultsLabel = staticText.getIn(
    ['data', 'noVideoResults'],
    'No video results found.',
  ) as string

  const isLoading = streamState === 'LOADING'
  const isStreaming = streamState === 'STREAMING'

  return (
    <EventContextProvider context="ai_search">
    <div className="search-page">
      {showOverview ? (
        <AiGuide
          staticText={staticText}
          streamingText={streamingText}
          loading={isLoading}
          isStreaming={isStreaming}
          statusKey={statusKey}
          error={aiError}
          refine={handleRefine}
          sendFeedback={sendFeedback}
          turnId={turnId}
        />
      ) : (
        <SearchForm
          autofocus
          className={['search-page__search-form']}
          hasButton
          hideFilters
          location={location}
          defaultSearchTerm={q}
          history={history}
          iconClass={['search-page__search-icon']}
        />
      )}

      {q ? (
        <div className="search-page__sort-row">
          <SortBar
            staticText={staticText}
            sortMode={sortMode}
            onSortChange={handleSortChange}
            disabled={isLoading}
          />
          <hr />
        </div>
      ) : null}

      {/* Blacklist message */}
      {blacklistMessage && (
        <p className="search-page__ai-empty">{blacklistMessage}</p>
      )}

      {/* Skeleton tiles during loading */}
      {!blacklistMessage && (isLoading || isStreaming) && tileData.size === 0 && (
        <div className="search-page__skeleton-tiles">
          {Array.from({ length: 16 }, (_, i) => (
            <div key={i} className="search-page__skeleton-tile">
              <div className="search-page__skeleton-tile-hero" />
            </div>
          ))}
        </div>
      )}

      {/* Results grid */}
      {!blacklistMessage && tileData.size > 0 && (
        <div className="search-page__results-container tile-grid-v2">
          <Row
            items={tileData}
            createAccessor={() => null}
            style={STYLES.S12345}
            asGrid
          >
            {(item: ImmutableMap<string, unknown>, params: { index: number }) => (
              <TileV2Graphql
                key={String(item.get('id', params.index))}
                data={item || ImmutableMap()}
                sectionIndex={0}
                rowPlacement={0}
                adminTitle="Search Results"
                index={params.index}
                upstreamContext={searchUpstreamContext}
              />
            )}
          </Row>
        </div>
      )}

      {/* No results */}
      {!blacklistMessage && !isLoading && !isStreaming && tileData.size === 0 && q && (
        <p className="search-page__ai-empty">{noVideoResultsLabel}</p>
      )}

      {/* Load More */}
      {!blacklistMessage && hasMore && tileData.size > 0 && (
        <div className="search-page__load-more">
          <Button
            type={BUTTON_TYPES.GHOST}
            onClick={aiLoadMore}
            disabled={isLoading}
          >
            {loadMoreLabel}
          </Button>
        </div>
      )}
    </div>
    </EventContextProvider>
  )
}

export default compose(
  connectPage({ seoType: PAGE_SEO_TYPE_LOCATION, storeBranch: '', storeKey: '' }),
  connectStaticText({ storeKey: 'searchPage' }),
)(SearchPage)
